import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getStorage } from '@/store/storage';
import { experienceItemKey, experienceProgressItems } from '@/constants/utils/experience_spec';

// Device-local, account/conversation/card-scoped progress. No shared-chat writes.
// Each item's storage key is independent, so simultaneous cards cannot overwrite
// one another's checkmarks. Revisions retain only unchanged item fingerprints.
export default function useExperienceProgress({ spec, userId, conversationId, messageId, canPersist }) {
  const items = useMemo(() => experienceProgressItems(spec), [spec]);
  const prefix = canPersist && userId && conversationId
    ? `guide-progress-v1:${[userId, conversationId, spec.kind, spec.id].map(encodeURIComponent).join(':')}:`
    : null;
  const keys = useMemo(() => items.map(experienceItemKey), [items]);
  const scope = `${prefix || [userId || 'guest', conversationId || messageId || 'unsaved', spec.kind, spec.id].map(encodeURIComponent).join(':')}:${keys.join(',')}`;
  const [state, setState] = useState({ scope, checked: {}, ready: !prefix, failed: false });
  const generation = useRef(0);
  const writes = useRef(Promise.resolve());
  useEffect(() => {
    const current = ++generation.current;
    setState({ scope, checked: {}, ready: !prefix, failed: false });
    if (!prefix) return;
    let cancelled = false;
    (async () => {
      try {
        const storage = getStorage();
        const values = await Promise.all(keys.map(key => Promise.resolve(storage.getItem(prefix + key))));
        if (!cancelled && generation.current === current) setState({ scope, checked: Object.fromEntries(keys.map((key, i) => [key, values[i] === '1'])), ready: true, failed: false });
      } catch (_) {
        if (!cancelled && generation.current === current) setState({ scope, checked: {}, ready: true, failed: true });
      }
    })();
    return () => { cancelled = true; };
  }, [scope, prefix, keys]);
  const active = state.scope === scope ? state : { checked: {}, ready: !prefix, failed: false };
  const toggle = useCallback(item => {
    if (!active.ready) return;
    const key = experienceItemKey(item);
    if (!keys.includes(key)) return;
    const next = !active.checked[key];
    setState(previous => ({ ...previous, checked: { ...previous.checked, [key]: next } }));
    if (!prefix) return;
    const current = generation.current;
    writes.current = writes.current.catch(() => {}).then(async () => {
      try {
        const storage = getStorage();
        if (next) await storage.setItem(prefix + key, '1');
        else await storage.removeItem(prefix + key);
      } catch (_) {
        if (generation.current === current) setState(previous => ({ ...previous, failed: true }));
      }
    });
  }, [active.checked, active.ready, keys, prefix]);
  return { checked: active.checked, toggle, ready: active.ready, failed: active.failed, persistent: !!prefix, total: keys.length, completed: keys.filter(key => active.checked[key]).length };
}
